/**
 * VFXComponent manages a VFXEmitter for use in the Genesys engine.
 *
 * This component loads VFX definitions from JSON and creates particle emitters.
 * The VFXParticles system is managed globally, and emitters are attached to actors.
 */

import { SceneComponent } from '../components/SceneComponent.js';
import { AssetPath } from '../storage/storageProvider.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../utils/IAsyncLoadable.js';
import { resourceManager } from '../utils/ResourceManager.js';
import { property } from '../utils/serialization/decorator.js';

import { VFXEmitterCore } from './core/index.js';
import { VFXDefinition } from './types.js';


function isSerializedResource(value: unknown): value is { $version: number; $root: unknown } {
  return typeof value === 'object' && value !== null &&
    '$version' in value &&
    typeof (value as { $version?: unknown }).$version === 'number' &&
    '$root' in value;
}

import type { VFXDefinitionJSON } from './types.js';
import type { SceneComponentOptions } from '../components/SceneComponent.js';
import type { IAsyncLoadable } from '../utils/IAsyncLoadable.js';
import type { VfxPath } from '../utils/serialization/decorator.js';
import type * as THREE from 'three';

export interface VFXComponentOptions extends SceneComponentOptions {
  /** Path to VFX JSON definition file */
  vfxPath?: string;
  /** Inline VFX definition (overrides vfxPath) */
  vfxDefinition?: VFXDefinition;
  /** Whether to start emitting automatically on begin play */
  autoStart?: boolean;
}

@EngineClass('VFXComponent')
export class VFXComponent extends SceneComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;

  @property()
  public vfxPath: VfxPath | null = null;

  @property()
  public autoStart: boolean = true;

  @property({ hidden: true, transient: true })
  private vfxEmitters: VFXEmitterCore[] = [];
  private isPlaying: boolean = false;
  private isReady: boolean = false;
  private pendingAutoStart: boolean = false;

  private _asyncTaskWorker: AsyncTaskWorker = new AsyncTaskWorker();

  private vfxDefinition: VFXDefinition | null = null; // Not meant to be serialized

  // Editor preview time tracking - independent of game time
  private editorPreviewElapsed: number = 0;


  public override initialize(options?: VFXComponentOptions): void {
    super.initialize(options);
    this.vfxPath = options?.vfxPath ?? this.vfxPath;
    this.vfxDefinition = options?.vfxDefinition ?? this.vfxDefinition;
    this.autoStart = options?.autoStart ?? this.autoStart;

    // Only load the definition - emitter creation happens in beginPlay when world exists
    if (this.vfxPath) {
      this.loadVFXDefinition(this.vfxPath);
    }
  }

  public override postLoad(): void {
    super.postLoad();
    // Only load the definition - emitter creation happens in beginPlay when world exists
    if (this.vfxPath) {
      this.loadVFXDefinition(this.vfxPath);
    }
  }

  // start IAsyncLoadable implementation
  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }
  // end IAsyncLoadable implementation

  private async loadVFXDefinition(path: string): Promise<void> {
    this._asyncTaskWorker.runTask(async () => {
      try {
        const json = await resourceManager.loadJsonObject(new AssetPath(path));
        if (!json) {
          throw new Error(`No VFX data found at ${path}`);
        }

        if (isSerializedResource(json)) {
          const loaded = await resourceManager.loadResource<VFXDefinition>(new AssetPath(path));
          this.vfxDefinition = loaded?.instance ?? null;
          return;
        }

        this.vfxDefinition = VFXDefinition.fromJSON(json as VFXDefinitionJSON);
      } catch (error) {
        console.error(`Failed to load VFX definition from ${path}:`, error);
      }
    });
  }

  private createVFXEmitter(): Promise<void> {
    return this._asyncTaskWorker.runTask(async () => {
      if (!this.vfxDefinition) return;

      const world = this.getWorld();
      if (!world) {
        console.warn('VFXComponent: Cannot create emitter without world');
        return;
      }

      const particleManager = world.globalParticleManager;

      // Create all particle systems (this loads textures/geometry)
      for (let i = 0; i < this.vfxDefinition.particles.length; i++) {
        const particleSettings = this.vfxDefinition.particles[i];
        const particleName = `${this.vfxDefinition.name}_particles_${i}`;
        await particleManager.getOrCreateVFXParticles(particleName, particleSettings);
      }

      // Create all emitters
      this.vfxEmitters = [];
      for (let i = 0; i < this.vfxDefinition.emitters.length; i++) {
        const emitterSettings = this.vfxDefinition.emitters[i];
        const particlesIndex = emitterSettings.particlesIndex ?? 0;
        const particleName = `${this.vfxDefinition.name}_particles_${particlesIndex}`;

        const emitter = new VFXEmitterCore(
          particleName,
          emitterSettings,
          particleManager.getVFXStore()
        ) as VFXEmitterCore & { isTransient?: () => boolean };

        // Mark as transient so serializer skips it
        emitter.isTransient = () => true;

        this.add(emitter);
        this.vfxEmitters.push(emitter);
      }

      // Mark as ready - all resources are now loaded
      this.isReady = true;

      // Handle pending auto start (if startEmitting was called before ready)
      if (this.pendingAutoStart) {
        this.pendingAutoStart = false;
        this.startEmitting(true);
      }
    });
  }

  public override beginPlay(): void {
    super.beginPlay();

    // If autoStart is enabled, queue it - will be handled once ready
    if (this.autoStart) {
      this.pendingAutoStart = true;
    }

    // Wait for definition to load, then create emitters
    this._asyncTaskWorker.waitUntilComplete().then(async () => {
      // Create emitters now that world is guaranteed to exist
      // This also loads textures/geometry and sets isReady=true when done
      if (this.vfxEmitters.length === 0 && this.vfxDefinition) {
        await this.createVFXEmitter();
      } else if (this.vfxEmitters.length > 0) {
        // Emitters already exist (e.g., inline definition), mark as ready
        this.isReady = true;
        if (this.pendingAutoStart) {
          this.pendingAutoStart = false;
          this.startEmitting(true);
        }
      }
    });
  }

  /**
   * Called when the component is added to an editor world.
   * Mirrors beginPlay() behavior for editor preview.
   */
  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();

    // Reset editor preview time
    this.editorPreviewElapsed = 0;

    // If autoStart is enabled, queue it - will be handled once ready
    if (this.autoStart) {
      this.pendingAutoStart = true;
    }

    // Wait for definition to load, then create emitters
    this._asyncTaskWorker.waitUntilComplete().then(async () => {
      // Create emitters now that world is guaranteed to exist
      if (this.vfxEmitters.length === 0 && this.vfxDefinition) {
        await this.createVFXEmitter();
      } else if (this.vfxEmitters.length > 0) {
        // Emitters already exist (e.g., inline definition), mark as ready
        this.isReady = true;
        if (this.pendingAutoStart) {
          this.pendingAutoStart = false;
          this.startEmitting(true);
        }
      }
    });
  }

  /**
   * Called when the component is removed from an editor world.
   * Tears down emitters and resets state for clean re-creation.
   */
  public override onEditorRemoveFromWorld(): void {
    super.onEditorRemoveFromWorld();

    // Stop emitting and reset state
    this.stopEmitting();
    this.isReady = false;
    this.pendingAutoStart = false;
    this.editorPreviewElapsed = 0;

    // Remove all emitter children
    for (const emitter of this.vfxEmitters) {
      this.remove(emitter);
    }
    this.vfxEmitters = [];
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    if (!this.isPlaying) {
      return;
    }

    const world = this.getWorld();
    let elapsedTime: number;

    if (this.isEditor) {
      // In editor worlds, use local preview time that advances continuously
      this.editorPreviewElapsed += deltaTime;
      elapsedTime = this.editorPreviewElapsed;
    } else {
      // In game worlds, use the world's game time
      elapsedTime = world?.getGameTime() ?? 0;
    }

    for (const emitter of this.vfxEmitters) {
      emitter.update(elapsedTime, deltaTime);
    }
  }

  public override endPlay(): void {
    this.stopEmitting();
    this.isReady = false;
    this.pendingAutoStart = false;
    this.editorPreviewElapsed = 0;
    super.endPlay();
  }

  /**
   * Start emitting particles.
   * If the VFX is not yet ready (still loading), the start will be queued
   * and executed automatically once loading completes.
   */
  public startEmitting(reset: boolean = true): void {
    if (!this.isReady) {
      // Queue the start for when loading completes
      this.pendingAutoStart = true;
      return;
    }

    for (const emitter of this.vfxEmitters) {
      emitter.startEmitting(reset);
    }
    this.isPlaying = true;
  }

  /**
   * Stop emitting particles
   */
  public stopEmitting(): void {
    for (const emitter of this.vfxEmitters) {
      emitter.stopEmitting();
    }
    this.isPlaying = false;
  }

  /**
   * Emit particles at a specific position (all emitters)
   */
  public emitAtPosition(position: THREE.Vector3, reset: boolean = false): void {
    for (const emitter of this.vfxEmitters) {
      emitter.emitAtPos(position, reset);
    }
  }

  public getVFXEmitters(): VFXEmitterCore[] {
    return this.vfxEmitters;
  }

  public getVFXEmitter(index: number): VFXEmitterCore | null {
    return this.vfxEmitters[index] ?? null;
  }

  public getVFXDefinition(): VFXDefinition | null {
    return this.vfxDefinition;
  }

  public getIsReady(): boolean {
    return this.isReady;
  }

  public async waitUntilReady(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Vfx';
  }
}
